import { useEffect, useMemo, useRef, useState } from 'react'
import type { CoverageLevel } from '@ispot/shared'
import type { useMarketSegmentation } from '@/hooks/useMarketSegmentation'
import { useMarketSegmentationViewStore } from '@/stores/marketSegmentationViewStore'
import { SegmentList } from '../segments/SegmentList'
import { SegmentDetail } from '../segments/SegmentDetail'
import { SegmentCardGrid } from '../segments/SegmentCardGrid'
import { SegmentDetailDrawer } from '../segments/SegmentDetailDrawer'
import { AddSegmentDialog } from '../segments/AddSegmentDialog'
import { ListEmptyState, SegmentsToolbar } from './SegmentsTabParts'

type Hook = ReturnType<typeof useMarketSegmentation>

interface Props {
  state: Hook
  selectedId?: string | null
  onSelectedChange?: (id: string | null) => void
  fullScreen?: boolean
  onToggleFullScreen?: () => void
}

export function SegmentsTab({
  state,
  selectedId: externalSelectedId,
  onSelectedChange,
  fullScreen = false,
  onToggleFullScreen,
}: Props) {
  const {
    canvas,
    segments,
    personas,
    coverage,
    isLoading,
    error,
    createSegment,
    updateSegment,
    deleteSegment,
    updateField,
    resetField,
    linkPersona,
    unlinkPersona,
    setBeachhead,
    generateScoring,
    generateSizing,
    generateBrief,
    listBriefs,
  } = state

  const view = useMarketSegmentationViewStore((s) => s.view)
  const setView = useMarketSegmentationViewStore((s) => s.setView)

  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const selectedId = externalSelectedId !== undefined ? externalSelectedId : internalSelectedId
  const setSelectedId = (id: string | null) => {
    setInternalSelectedId(id)
    onSelectedChange?.(id)
  }

  // Auto-select first segment in list view only. Cards view keeps the drawer
  // closed until the user clicks a card (or arrives with a pre-set selection).
  useEffect(() => {
    if (view !== 'list') return
    if (segments.length === 0) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    const stillThere = segments.some((s) => s.id === selectedId)
    if (!stillThere) setSelectedId(segments[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, selectedId, view])

  // When toggling list → cards, clear selection so the drawer doesn't pop
  // open on a segment the list had auto-selected. Skip on first mount so a
  // pre-set selection (e.g. from the Overview tab) still opens.
  const viewInitRef = useRef(true)
  useEffect(() => {
    if (viewInitRef.current) {
      viewInitRef.current = false
      return
    }
    if (view === 'cards') setSelectedId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  const selectedSegment = useMemo(
    () => segments.find((s) => s.id === selectedId) ?? null,
    [segments, selectedId],
  )

  // Keyboard nav: ↑/↓ cycle segments in list view only.
  useEffect(() => {
    if (view !== 'list') return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (segments.length === 0) return
      const idx = segments.findIndex((s) => s.id === selectedId)
      const next = e.key === 'ArrowDown'
        ? segments[Math.min(segments.length - 1, (idx < 0 ? 0 : idx + 1))]
        : segments[Math.max(0, (idx < 0 ? 0 : idx - 1))]
      if (next && next.id !== selectedId) {
        e.preventDefault()
        setSelectedId(next.id)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, selectedId, view])

  const beachheadId = canvas?.beachheadSegmentId ?? null
  const previousBeachheadName = useMemo(() => {
    if (!beachheadId) return null
    return segments.find((s) => s.id === beachheadId)?.name ?? null
  }, [beachheadId, segments])

  if (isLoading && !canvas) {
    return (
      <div
        className="flex h-full items-center justify-center text-sm"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Loading canvas…
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p
          className="max-w-md rounded border px-4 py-3 text-sm"
          style={{ borderColor: '#f4c9c5', backgroundColor: '#fdf0ee', color: '#8b2d20' }}
        >
          {error}
        </p>
      </div>
    )
  }

  const detailProps = selectedSegment
    ? {
        segment: selectedSegment,
        isBeachhead: selectedSegment.id === beachheadId,
        previousBeachheadName:
          beachheadId === selectedSegment.id ? null : previousBeachheadName,
        personas,
        coverage,
        fullScreen,
        onUpdateSegment: async (updates: Parameters<Hook['updateSegment']>[1]) => {
          await updateSegment(selectedSegment.id, updates)
        },
        onUpdateField: (
          path: 'sizing' | 'scoring',
          key: string,
          value: number | null,
        ) => updateField(selectedSegment.id, path, key, value),
        onResetField: (path: 'sizing' | 'scoring', key: string) =>
          resetField(selectedSegment.id, path, key),
        onLinkPersona: async (personaId: string, level: CoverageLevel) => {
          await linkPersona(selectedSegment.id, personaId, level)
        },
        onUnlinkPersona: (personaId: string) => unlinkPersona(selectedSegment.id, personaId),
        onSetBeachhead: setBeachhead,
        onDelete: () => deleteSegment(selectedSegment.id),
        onToggleFullScreen,
        onGenerateScoring: async () => {
          await generateScoring(selectedSegment.id)
        },
        onGenerateSizing: async () => {
          await generateSizing(selectedSegment.id)
        },
        onGenerateBrief: () => generateBrief(selectedSegment.id),
        onListBriefs: () => listBriefs(selectedSegment.id),
      }
    : null

  // Fullscreen short-circuits the regular view: render SegmentDetail filling
  // the entire tab content area, no toolbar, no drawer, no grid.
  if (fullScreen && detailProps) {
    return (
      <div className="flex h-full flex-col">
        <SegmentDetail {...detailProps} />
      </div>
    )
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <SegmentsToolbar
          view={view}
          onViewChange={setView}
          onAddClick={() => setDialogOpen(true)}
          showAddButton={view === 'cards'}
          segmentCount={segments.length}
        />

        {view === 'list' ? (
          <div className="flex min-h-0 flex-1">
            <SegmentList
              segments={segments}
              selectedId={selectedId}
              beachheadId={beachheadId}
              onSelect={setSelectedId}
              onAddClick={() => setDialogOpen(true)}
            />
            {detailProps ? (
              <SegmentDetail {...detailProps} />
            ) : (
              <ListEmptyState onAddClick={() => setDialogOpen(true)} />
            )}
          </div>
        ) : (
          <div className="min-h-0 flex-1">
            <SegmentCardGrid
              segments={segments}
              personas={personas}
              coverage={coverage}
              beachheadId={beachheadId}
              selectedId={selectedId}
              onOpen={setSelectedId}
              onAddClick={() => setDialogOpen(true)}
            />
          </div>
        )}
      </div>

      {view === 'cards' && detailProps ? (
        <SegmentDetailDrawer
          open={selectedId !== null}
          onClose={() => setSelectedId(null)}
          detailProps={detailProps}
        />
      ) : null}

      <AddSegmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreate={async (input) => {
          const seg = await createSegment(input)
          setSelectedId(seg.id)
        }}
      />
    </>
  )
}
